'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { X, ArrowRight, ArrowLeft, Bot, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { createPortal } from 'react-dom';

type TourStep = {
  id: string;
  title: string;
  description: string;
  targetId: string;
};

const TOUR_STEPS: TourStep[] = [
  { id: 'demo', title: 'Begin the Journey', description: 'Schedule your child\'s free trial class right here! This is the first step to unlocking their full potential.', targetId: 'nav-book-a-free-demo' },
  { id: 'home', title: 'Home', description: 'Access our main features and latest updates from this dashboard.', targetId: 'nav-home' },
  { id: 'about', title: 'About Us', description: 'Learn about our mission, vision, and the passion behind Upbeat Brain Game.', targetId: 'nav-about-us' },
  { id: 'blog', title: 'Learning Blog', description: 'Explore expert insights on child development and brain training techniques.', targetId: 'nav-blog' },
  { id: 'programs', title: 'Programs', description: 'Discover our 10+ courses tailored for children aged 5-15.', targetId: 'nav-programs' },
  { id: 'upbot', title: 'UpBot Support', description: 'Need instant help? UpBot is available 24/7 to answer your questions and guide you.', targetId: 'chatbot-trigger' },
];

export function SiteTour() {
  const [currentStep, setCurrentStep] = useState(-1);
  const [isVisible, setIsVisible] = useState(false);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    
    const handleStartTour = () => {
      setIsVisible(true);
      setCurrentStep(0);
    };

    window.addEventListener('start-site-tour', handleStartTour);
    
    return () => window.removeEventListener('start-site-tour', handleStartTour);
  }, []);

  const updateTargetRect = useCallback(() => {
    if (typeof window === 'undefined' || !mounted || currentStep < 0 || currentStep >= TOUR_STEPS.length) {
      setTargetRect(null);
      return;
    }

    const step = TOUR_STEPS[currentStep];
    let target = document.getElementById(step.targetId);

    // Check variants for mobile/tablet
    if (!target || target.offsetParent === null) {
      const mobileTarget = document.getElementById(`nav-mobile-${step.targetId.split('nav-')[1]}`);
      const tabletTarget = document.getElementById(`nav-tablet-${step.targetId.split('nav-')[1]}`);
      target = mobileTarget || tabletTarget || document.getElementById('mobile-menu-trigger');
    }

    if (target) {
      setTargetRect(target.getBoundingClientRect());
    } else {
      setTargetRect(null);
    }
  }, [currentStep, mounted]);

  useEffect(() => {
    if (mounted && isVisible) {
      updateTargetRect();
      window.addEventListener('resize', updateTargetRect);
      window.addEventListener('scroll', updateTargetRect);
      return () => {
        window.removeEventListener('resize', updateTargetRect);
        window.removeEventListener('scroll', updateTargetRect);
      };
    }
  }, [updateTargetRect, mounted, isVisible]);

  const handleNext = () => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setCurrentStep(-1);
    localStorage.setItem('upbeat_tour_seen', 'true');
  };

  const tooltipPosition = useMemo(() => {
    if (typeof window === 'undefined' || !targetRect) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    const padding = 16;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = Math.min(340, viewportWidth - 40);
    
    let top = targetRect.bottom + padding;
    let left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);

    if (left < 10) left = 10;
    if (left + tooltipWidth > viewportWidth) left = viewportWidth - tooltipWidth - 10;
    
    if (top + 220 > viewportHeight) {
        top = Math.max(10, targetRect.top - padding - 220);
    }

    if (TOUR_STEPS[currentStep]?.targetId === 'chatbot-trigger') {
        top = Math.max(10, targetRect.top - 240);
        left = Math.max(10, viewportWidth - tooltipWidth - 20);
    }

    return {
      top: `${top}px`,
      left: `${left}px`,
      width: `${tooltipWidth}px`,
      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    };
  }, [targetRect, currentStep]);

  if (!mounted || !isVisible) return null;

  return createPortal(
    <div className="fixed inset-0 z-[100] pointer-events-none overflow-hidden">
      <div 
        className="absolute inset-0 bg-black/60 transition-opacity duration-500 pointer-events-auto"
        style={{
            clipPath: targetRect ? 
                `polygon(0% 0%, 0% 100%, ${targetRect.left}px 100%, ${targetRect.left}px ${targetRect.top}px, ${targetRect.right}px ${targetRect.top}px, ${targetRect.right}px ${targetRect.bottom}px, ${targetRect.left}px ${targetRect.bottom}px, ${targetRect.left}px 100%, 100% 100%, 100% 0%)` : 
                'none'
        }}
        onClick={handleClose}
      />

      <Card 
        className="absolute shadow-2xl border-primary/30 bg-background/95 backdrop-blur-md pointer-events-auto animate-fade-in-up"
        style={tooltipPosition as React.CSSProperties}
      >
        <CardHeader className="pb-2 relative">
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute right-2 top-2 h-8 w-8 rounded-full hover:bg-muted" 
            onClick={handleClose}
          >
            <X className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 text-primary">
            {TOUR_STEPS[currentStep]?.id === 'upbot' ? <Bot className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
            <CardTitle className="text-lg font-headline">
              {TOUR_STEPS[currentStep]?.title}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="pb-4">
          <p className="text-sm text-muted-foreground leading-relaxed font-medium">
            {TOUR_STEPS[currentStep]?.description}
          </p>
        </CardContent>
        <CardFooter className="flex justify-between items-center bg-muted/30 pt-3">
          <div className="flex gap-1">
            {TOUR_STEPS.map((_, idx) => (
                <div 
                    key={idx} 
                    className={cn(
                        "h-1.5 rounded-full transition-all duration-300",
                        idx === currentStep ? "w-4 bg-primary" : "w-1.5 bg-primary/20"
                    )}
                />
            ))}
          </div>
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button variant="ghost" size="sm" onClick={handlePrev} className="h-8 font-bold">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            )}
            <Button size="sm" variant="accent" onClick={handleNext} className="h-8 font-bold">
              {currentStep === TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </CardFooter>
      </Card>
      
      {targetRect && (
        <div 
            className="absolute border-4 border-primary rounded-lg pointer-events-none animate-pulse shadow-[0_0_25px_rgba(59,130,246,0.8)]"
            style={{
                top: `${targetRect.top - 6}px`,
                left: `${targetRect.left - 6}px`,
                width: `${targetRect.width + 12}px`,
                height: `${targetRect.height + 12}px`,
                transition: 'all 0.4s ease-in-out',
            }}
        />
      )}
    </div>,
    document.body
  );
}
